import type { AccountEntity } from '../entities/accountEntity';
import { filterMutedScanErrors } from './mutedScanErrors';

/**
 * Summary of an account's synchronization health, derived purely from the
 * catalog entity so it can be rendered anywhere (header chips, resource
 * badges) without API access. Muted scan errors (spec.mutedErrors) do not
 * degrade the state.
 */
export type AccountSyncState = {
  /** Semantic tone for chips/badges: success, warning or neutral. */
  tone: 'success' | 'warning' | 'neutral';
  label: string;
  errorCount: number;
  mutedErrorCount: number;
};

/**
 * Derives the sync state of an account from its entity: a successful sync is
 * a `lastSyncTime` without (unmuted) scan errors, any unmuted recorded
 * errors mark the state as degraded, and a missing `lastSyncTime` means the
 * account never synced.
 */
export function getAccountSyncState(
  account: AccountEntity | undefined,
): AccountSyncState {
  const mutedErrors = account?.spec.mutedErrors ?? [];
  const errors = filterMutedScanErrors(account?.spec.errors, mutedErrors);
  const errorCount = errors.length;
  if (!account?.spec.lastSyncTime) {
    return {
      tone: 'neutral',
      label: 'Never synced',
      errorCount,
      mutedErrorCount: mutedErrors.length,
    };
  }
  if (errorCount > 0) {
    return {
      tone: 'warning',
      label: `Sync errors (${errorCount})`,
      errorCount,
      mutedErrorCount: mutedErrors.length,
    };
  }
  return {
    tone: 'success',
    label: 'Healthy sync',
    errorCount,
    mutedErrorCount: mutedErrors.length,
  };
}
